import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { toastAdvertencia, toastError, toastExito } from "@/lib/toast";
import { extractApiMessage } from "@/shared/api/extract-message";
import {
  cancelarMiSolicitudCatalogo,
  crearSolicitudCatalogo,
  listarTiposActivosSolicitudCatalogo,
  obtenerEstadoSolicitudCatalogo,
  type CrearSolicitudCatalogoInput,
} from "../services/solicitudes-catalogo.api";

/** Tipos ACTIVOS con sus textos · cambia poco (lo edita el administrador). */
export const tiposSolicitudCatalogoActivosQueryOptions = queryOptions({
  queryKey: qk.tiposSolicitudCatalogoActivos,
  queryFn: listarTiposActivosSolicitudCatalogo,
  staleTime: 5 * 60_000,
});

export function useTiposSolicitudCatalogoActivos(enabled = true) {
  return useQuery({ ...tiposSolicitudCatalogoActivosQueryOptions, enabled });
}

/**
 * Estado de la solicitud para (tipo, clave[, sociedad]) · decide si la UI muestra el botón
 * de solicitar o el aviso de que ya hay una pendiente.
 */
export function useEstadoSolicitudCatalogo(tipo: string, clave: string, numeroSociedad?: string, enabled = true) {
  return useQuery({
    queryKey: qk.solicitudCatalogoEstado(tipo, clave.toUpperCase(), numeroSociedad),
    queryFn: () => obtenerEstadoSolicitudCatalogo(tipo, clave, numeroSociedad),
    enabled,
    staleTime: 30_000,
  });
}

export function useCrearSolicitudCatalogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CrearSolicitudCatalogoInput) => crearSolicitudCatalogo(input),
    onSuccess: (resultado, input) => {
      void qc.invalidateQueries({
        queryKey: qk.solicitudCatalogoEstado(input.tipo, input.clave.toUpperCase(), input.numeroSociedad),
      });
      void qc.invalidateQueries({ queryKey: qk.misSolicitudesCatalogo });
      if (resultado.yaExistia) {
        toastAdvertencia({
          title: "Ya estaba solicitado",
          message: "Ya existe una solicitud pendiente para este elemento · los responsables ya la tienen en su bandeja.",
        });
      } else {
        toastExito({
          title: "Solicitud enviada",
          message: "Los responsables del catálogo recibieron tu solicitud en su bandeja.",
        });
      }
    },
    onError: (error: unknown) => {
      toastError({ title: "No se pudo enviar la solicitud", message: extractApiMessage(error, "Intenta de nuevo.") });
    },
  });
}

/** El solicitante cancela su solicitud pendiente (deja de verse en la bandeja · queda auditado). */
export function useCancelarSolicitudCatalogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo?: string }) => cancelarMiSolicitudCatalogo(id, motivo),
    onSuccess: (s) => {
      void qc.invalidateQueries({
        queryKey: qk.solicitudCatalogoEstado(s.tipo, s.clave, s.numeroSociedad ?? undefined),
      });
      void qc.invalidateQueries({ queryKey: qk.misSolicitudesCatalogo });
      toastExito({ title: "Solicitud cancelada", message: "Ya no aparecerá en la bandeja de los responsables." });
    },
    onError: (error: unknown) => {
      toastError({ title: "No se pudo cancelar", message: extractApiMessage(error, "Intenta de nuevo.") });
    },
  });
}
